import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  parseEndNodeId,
  type ProcessDraftAssignment,
  type ProcessDraftStep
} from '@bliks-io/contracts';
import {
  groupRulesByOutput,
  isRuleHandleId,
  parseRuleHandleId,
  type CanvasNodeKind
} from '@bliks-io/ui';
import { canMarkAsEnd } from '../canMarkAsEnd';
import { hasAssignmentAnchorInput, hasDownstream } from '../edge-actions/shared';
import { expandRuleIdToOutputGroup } from '../graph/expandRuleIdToOutputGroup';
import type { UndoableEditorActions } from './useUndoableEditorActions';

export interface NodeContextMenuHandlers {
  handleEditNode: (nodeId: string) => void;
  handleMarkAsEnd: (nodeId: string) => void;
  canMarkAsEndForNode: (nodeId: string, nodeKind: CanvasNodeKind) => boolean;
  handleConvertToAssignment: (nodeId: string) => void;
  canConvertToAssignmentForNode: (nodeId: string) => boolean;
  handleConvertToStep: (nodeId: string) => void;
  canConvertToStepForNode: (nodeId: string) => boolean;
  /** Open the merge picker for this node (it chooses the target + survivor). */
  handleMergeNode: (nodeId: string, nodeKind: CanvasNodeKind) => void;
  /**
   * Visibility predicate for "Merge into…". Only real mergeable cards
   * (task / decision steps and assignments) qualify, and only when at least
   * two cards exist — with a single card there is nothing to merge into.
   */
  canMergeNodeForNode: (nodeId: string, nodeKind: CanvasNodeKind) => boolean;
  /**
   * Reason "Merge into…" is disabled for a node, or `null` when allowed. Merge
   * is offered on leaf nodes only — see {@link useNodeContextMenuHandlers} body.
   */
  mergeNodeDisabledReasonForNode: (nodeId: string, nodeKind: CanvasNodeKind) => string | null;
  /**
   * Visibility predicate for the delete entry. Used to hide "Delete" on
   * synthetic nodes that can't be directly removed (e.g. amber UNCLEAR
   * end-nodes, which are driven by the predecessor's successor status).
   */
  canDeleteNode: (nodeId: string, nodeKind: CanvasNodeKind) => boolean;
  /**
   * Visibility predicate for "Add event for step". Only real draft steps
   * (tasks and decisions) can accept event inputs — draft assignments derive
   * inputs from their upstream, event nodes can't feed other events, and
   * end-nodes aren't real entities.
   */
  canAddEventForNode: (nodeId: string, nodeKind: CanvasNodeKind) => boolean;
  /**
   * Flip every rule behind a decision gateway's output handle to a terminal
   * state. Expands `handleId` (`rule-<representativeId>`) to all rules in
   * that output group so rules that visually share a single line on screen
   * can't end up in mismatched terminal states.
   */
  handleSetHandleTerminal: (nodeId: string, handleId: string, status: 'NONE' | 'UNCLEAR') => void;
  canSetHandleTerminal: (nodeId: string, handleId: string) => boolean;
}

/**
 * Wires the context menu's node-kind discrimination. `steps` and
 * `draftAssignments` are passed separately so the capability predicates can
 * tell which collection a node lives in:
 *  - "Mark as end" applies to task steps only.
 *  - "Convert to assignment" applies to steps with an upstream anchor input
 *    (a pre-condition for the server-side conversion).
 *  - "Convert to step" applies to assignments only.
 */
export function useNodeContextMenuHandlers(params: {
  undoableActions: UndoableEditorActions;
  steps: ProcessDraftStep[];
  draftAssignments: ProcessDraftAssignment[];
  startEventIds: ReadonlySet<string>;
  onSelectStep: (stepId: string) => void;
  onOpenMergePicker: (nodeId: string, nodeKind: CanvasNodeKind) => void;
}): NodeContextMenuHandlers {
  const {
    undoableActions,
    steps,
    draftAssignments,
    startEventIds,
    onSelectStep,
    onOpenMergePicker
  } = params;
  const { t } = useTranslation();
  const handleEditNode = useCallback((nodeId: string) => onSelectStep(nodeId), [onSelectStep]);

  const handleMarkAsEnd = useCallback(
    (nodeId: string) => {
      const parsed = parseEndNodeId(nodeId);
      if (parsed) {
        // When the end-node is a rule branch, flip the whole rule output group
        // so rules sharing a gateway handle stay in sync.
        if (parsed.branchKey) {
          const step = steps.find((s) => String(s.stableStepId) === parsed.predecessorStableId);
          const expanded = step ? expandRuleIdToOutputGroup(step.rules, parsed.branchKey) : [];
          const ruleIds = expanded.length > 0 ? expanded : [parsed.branchKey];
          void undoableActions.markRulesAsTerminal(parsed.predecessorStableId, ruleIds, 'NONE');
        } else {
          void undoableActions.markStepAsTerminal(parsed.predecessorStableId, 'NONE');
        }
        return;
      }
      void undoableActions.markStepAsTerminal(nodeId, 'NONE');
    },
    [undoableActions, steps]
  );

  const canMarkAsEndForNode = useCallback(
    (nodeId: string, nodeKind: CanvasNodeKind) => {
      // Only amber UNCLEAR ends reach the menu, and "Mark as end" resolves
      // them by flipping the predecessor's successor status.
      if (nodeKind === 'end') return true;
      if (nodeKind !== 'step') return false;
      return canMarkAsEnd(nodeId, steps);
    },
    [steps]
  );

  const isAssignment = useCallback(
    (nodeId: string) => draftAssignments.some((a) => String(a.stableStepId) === nodeId),
    [draftAssignments]
  );

  const handleConvertToAssignment = useCallback(
    (nodeId: string) => {
      void undoableActions.convertStepToAssignment(nodeId);
    },
    [undoableActions]
  );

  const canConvertToAssignmentForNode = useCallback(
    (nodeId: string) => {
      const step = steps.find((s) => String(s.stableStepId) === nodeId);
      if (!step) return false;
      // Assignments require an upstream anchor — without one the server
      // conversion would fail validation (and an unanchored assignment is
      // meaningless since it represents a response to an upstream output).
      if (!hasAssignmentAnchorInput(step.inputs, startEventIds)) return false;
      // Only offered on leaf steps: an assignment is an unrecorded placeholder,
      // so converting a step that already has authored downstream work would
      // orphan those children with no owning task to return to on undo.
      if (hasDownstream(nodeId, steps, draftAssignments)) return false;
      return true;
    },
    [steps, draftAssignments, startEventIds]
  );

  const handleConvertToStep = useCallback(
    (nodeId: string) => {
      void undoableActions.convertAssignmentToStep(nodeId);
    },
    [undoableActions]
  );

  const canConvertToStepForNode = useCallback(
    (nodeId: string) => isAssignment(nodeId),
    [isAssignment]
  );

  const handleMergeNode = useCallback(
    (nodeId: string, nodeKind: CanvasNodeKind) => onOpenMergePicker(nodeId, nodeKind),
    [onOpenMergePicker]
  );

  const canMergeNodeForNode = useCallback(
    (_nodeId: string, nodeKind: CanvasNodeKind) => {
      // Only real cards can merge; events / end / lane / synthetic nodes can't.
      if (nodeKind !== 'step' && nodeKind !== 'decision' && nodeKind !== 'assignment') {
        return false;
      }
      // Need a second card to merge into — hide on a one-node draft.
      return steps.length + draftAssignments.length >= 2;
    },
    [steps, draftAssignments]
  );

  const mergeNodeDisabledReasonForNode = useCallback(
    (nodeId: string): string | null => {
      // Product rule: merge is offered on leaf nodes only. A merge rewires the
      // node's downstream referrers onto the survivor and drops the merged
      // node's own recorded content — restricting to leaves keeps users from
      // unexpectedly re-parenting downstream steps onto a different node.
      if (hasDownstream(nodeId, steps, draftAssignments)) {
        return t('processFlow.contextMenu.mergeDisabledHasSuccessor');
      }
      return null;
    },
    [steps, draftAssignments, t]
  );

  const handleSetHandleTerminal = useCallback(
    (nodeId: string, handleId: string, status: 'NONE' | 'UNCLEAR') => {
      const representativeId = parseRuleHandleId(handleId);
      if (!representativeId) return;
      const step = steps.find((s) => String(s.stableStepId) === nodeId);
      if (!step?.rules?.length) return;
      const grouped = groupRulesByOutput(
        step.rules.map((r) => ({
          _id: r._id ? String(r._id) : undefined,
          output: r.output
        }))
      );
      const ruleIds: string[] = [];
      for (const [ruleId, rep] of grouped.ruleIdToRepresentative) {
        if (rep === representativeId) ruleIds.push(ruleId);
      }
      if (ruleIds.length === 0) return;
      void undoableActions.markRulesAsTerminal(nodeId, ruleIds, status);
    },
    [steps, undoableActions]
  );

  const canSetHandleTerminal = useCallback(
    (_nodeId: string, handleId: string) => isRuleHandleId(handleId),
    []
  );

  // Removing an unclear end-node means flipping the predecessor out of
  // UNCLEAR (via Mark-as-end or the assignee picker), not deleting the marker.
  // The start event is non-deletable (backend ≥1-start invariant) — don't
  // offer a guaranteed-failing action; keyboard Delete is caught gracefully
  // in useDraftNodeDeletion regardless. Everything else with a menu is a
  // deletable entity (steps, decisions, assignments, other event kinds).
  const canDeleteNodeForNode = useCallback((_nodeId: string, nodeKind: CanvasNodeKind) => {
    switch (nodeKind) {
      case 'end':
      case 'event-start':
      case 'empty-step':
        return false;
      case 'step':
      case 'decision':
      case 'assignment':
      case 'event-intermediate':
      case 'event-legacy':
      case 'swimlane':
      case 'synthetic-gateway':
        return true;
      case 'note':
        // Notes are deleted via their own handler, not this node menu.
        return false;
      default: {
        nodeKind satisfies never;
        return false;
      }
    }
  }, []);

  // Events attach to a step's `inputs` — only real draft steps (tasks and
  // decisions) carry one. Assignments derive inputs from their upstream
  // `previous_output`, event nodes can't feed other events, and end/lane/
  // synthetic nodes aren't real entities.
  const canAddEventForNode = useCallback((_nodeId: string, nodeKind: CanvasNodeKind) => {
    switch (nodeKind) {
      case 'step':
      case 'decision':
        return true;
      case 'assignment':
      case 'event-start':
      case 'event-intermediate':
      case 'event-legacy':
      case 'end':
      case 'swimlane':
      case 'synthetic-gateway':
      case 'note':
      case 'empty-step':
        return false;
      default: {
        nodeKind satisfies never;
        return false;
      }
    }
  }, []);

  return {
    handleEditNode,
    handleMarkAsEnd,
    canMarkAsEndForNode,
    handleConvertToAssignment,
    canConvertToAssignmentForNode,
    handleConvertToStep,
    canConvertToStepForNode,
    handleMergeNode,
    canMergeNodeForNode,
    mergeNodeDisabledReasonForNode,
    canDeleteNode: canDeleteNodeForNode,
    canAddEventForNode,
    handleSetHandleTerminal,
    canSetHandleTerminal
  };
}
